import { changedKeys, serializeForDiff, type DiffRecord as Obj } from './jsonDiff';
import './JsonDiffView.css';

/**
 * Side-by-side JSON diff for the config confirm dialogs. It names the settings
 * that change, then marks every line that belongs to one of them: red on the
 * "Current" side (the value that goes), green on the "New" side (the value that
 * arrives). Nested blocks (apps, groups, budget) are marked whole because a
 * line takes the last depth-1 key above it.
 *
 * The serialization itself lives in `./jsonDiff` so it can be unit-tested
 * without a DOM.
 */

function DiffColumn({ obj, changed, side }: { obj: Obj; changed: Set<string>; side: 'before' | 'after' }) {
  const lines = serializeForDiff(obj).split('\n');
  let currentKey: string | null = null;
  return (
    <pre className="json-diff__code">
      {/* The inner block is as wide as the longest line, so a mark runs the full width when the block scrolls. */}
      <div className="json-diff__lines">
        {lines.map((line, i) => {
          // Root braces close the key context; a depth-1 `  "key":` opens one.
          if (line === '{' || line === '}') currentKey = null;
          const keyMatch = line.match(/^ {2}"([^"]+)":/);
          if (keyMatch) currentKey = keyMatch[1];
          const highlighted = currentKey !== null && changed.has(currentKey);
          return (
            <div key={i} className={`json-diff__line${highlighted ? ` json-diff__line--${side}` : ''}`}>
              {line || ' '}
            </div>
          );
        })}
      </div>
    </pre>
  );
}

export function JsonDiffView({ before, after }: { before: Obj; after: Obj }) {
  const changed = changedKeys(before, after);
  const keys = [...changed].sort();
  return (
    <div className="json-diff">
      {keys.length === 0 ? (
        <p className="json-diff__summary">Nothing changes.</p>
      ) : (
        <>
          <p className="json-diff__summary">
            {keys.length === 1 ? '1 setting changes:' : `${keys.length} settings change:`}{' '}
            {keys.map((k) => (
              <code key={k} className="json-diff__key">
                {k}
              </code>
            ))}
          </p>
          <div className="json-diff__legend">
            <span className="json-diff__swatch json-diff__swatch--before" aria-hidden="true" />
            Old value, or removed
            <span className="json-diff__swatch json-diff__swatch--after" aria-hidden="true" />
            New value, or added
          </div>
        </>
      )}
      <div className="json-diff__columns">
        <div className="json-diff__column">
          <h3 className="section-label">Current</h3>
          <DiffColumn obj={before} changed={changed} side="before" />
        </div>
        <div className="json-diff__column">
          <h3 className="section-label">New</h3>
          <DiffColumn obj={after} changed={changed} side="after" />
        </div>
      </div>
    </div>
  );
}

export default JsonDiffView;
